import React, { Component, FC, useContext, useState } from 'react'
import { observer } from 'mobx-react'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import _ from 'underscore'
import { t } from 'ttag'
import _debug from 'debug'

import { displayError, systemError, ErrorBoundary } from '../utils/Errors'
import { Root } from '../../models3/Root'
import PassageView from './PassageView'
import PassageAdder from './PassageAdder'
import PortionSelector from './PortionSelector'
import { Passage, Portion } from '../../models3/ProjectModels'
import { RootConsumer, RootContext } from '../app/RootContext'
import VideoDropTarget from './VideoDropTarget'
import { DropTargetViewSmall } from '../utils/DropTargetView'
import './Passage.css'
import { useHoverDelay } from '../utils/Hooks'
import PassageEditor from './PassageEditor'
import { confirmUnremove, unRemovedObjectTooltip } from '../portions/PortionsEditor'
import { UnremoveIcon } from '../utils/Icons'

const log = _debug('sltt:PassageList') 

interface IPassageUnremover {
    removedPassage: Passage,
}

const PassageUnremover: FC<IPassageUnremover> = observer(({ removedPassage }) => {
    const rt = useContext(RootContext)

    const clickTip = t`Click to un-delete this Passage.`

    const title = /* translator: important */ t`Un-delete passage?`
    const tooltip = unRemovedObjectTooltip(rt!, removedPassage)

    async function doUnremove() {
        // find the portion based on the passage _id
        const portion = rt?.project.findPortion(removedPassage._id)
        if (!portion) {
            log('### No portion found for passage:', removedPassage)
            return
        }

        await portion.unremovePassage(removedPassage)
        rt?.setPassage(removedPassage) // select Passage and scroll it into view
    }

    return (
        <div className="passages-list">
            {
                <div className="passage-deleted">
                    <div className="passage-name removed-passage-name"
                        onClick={() => confirmUnremove(rt!, removedPassage, title, doUnremove)}
                        data-toggle="tooltip"
                        title={tooltip + clickTip}>
                            {removedPassage.name}
                            <UnremoveIcon className="portion-unremove-icon" tooltip={tooltip + ' ' + clickTip} />
                    </div>
                </div>
            }
        </div>
    )
})

interface IPassagesUnremover {
    rt: Root,
    removedPassages: Passage[],
}

const PassagesUnremover: FC<IPassagesUnremover> = observer(({ removedPassages }) => {
    if (removedPassages.length === 0) return null

    return (
        <div className="passages-list">
            <hr className="passages-hr" />
            {
                removedPassages.filter(p => p.name).map((passage, index) => (
                    <PassageUnremover removedPassage={passage} key={index} />
                ))
            }
            <hr />
        </div>
    )
})

// Forced DragHandle and Sortable item to type 'any' to supress bogus typescript error

const DragHandle: any = SortableHandle(() =>
    <RootConsumer>
        {rt => (
            <span className="passage-handle"
                data-toggle="tooltip"
                title={t`Drag to reorder.`}>
                <i className="fas fa-bars"></i>
            </span>
        )}
    </RootConsumer>
)

interface ISortableElement {
    rt: Root,
    passage: Passage,
}

const SortableItem: any = SortableElement(
    function (props: ISortableElement) {
        const [editing, setEditing] = useState(false)
        const [mouseOver, setMouseOver] = useState(false)
        const showEditingIcons = useHoverDelay(mouseOver, 50)

        let message = <div />
        let dropTargetView = <DropTargetViewSmall message={message} />
        let { rt, passage } = props
        let { useMobileLayout, username } = rt
        let assigned = passage.assignee === username && passage.task !== 'Finished'
        return (
            <VideoDropTarget rt={rt} passage={props.passage} videoIsPatch={false} dropTargetView={dropTargetView}>
                <div
                    className={`passage passage-list-item ${assigned ? 'passage-assigned' : ''}`}
                    data-id={'passage-' + props.passage.name}
                    onMouseEnter={() => setMouseOver(true)}
                    onMouseLeave={() => setMouseOver(false)}
                >
                    {!useMobileLayout && !editing && <DragHandle />}
                    <PassageEditor {...{ rt, passage, editing, setEditing }} />
                    <PassageView {...{ rt, passage, editing, setEditing, showEditingIcons }} />
                </div>
            </VideoDropTarget>
        )
    }
)

interface ISortableContainer {
    rt: Root,
    items: Array<Passage>,
}

const SortableList = SortableContainer(
    (props: ISortableContainer) => {
        return (
            <div>
                {props.items.map((passage, index) => (
                    <ErrorBoundary
                        key={passage._id}
                        fallbackUI={(
                            <div
                                className='passage passage-error'
                                data-id={'passage-' + passage.name}
                                title={t`Something went wrong while displaying this passage`}
                            >
                                <b>{`${passage.name}???`}</b>
                            </div>
                        )}
                        fallbackAction={() => {
                            props.rt.setPassage(null)
                        }}
                    >
                        <SortableItem
                            index={index}
                            rt={props.rt}
                            passage={passage} />
                    </ErrorBoundary>
                ))}
            </div>
        )
    }
)

interface IExportTooltipVideoIds {
    rt: Root,
}

const ExportTooltipVideoIds: FC<IExportTooltipVideoIds> = ({ rt }) => {
    function _download() {
        let ids: string[] = []
        for (let portion of rt.project.portions) {
            for (let passage of portion.passages) {
                let video = passage.getLatestVideo()
                if (video) {
                    ids.push(`"${passage.name}|${video.url}"`)
                }
            }
        }

        let data = `export const videoTooltipIds = [${ids.join(',\n')}];`
        let blob = new Blob([data], { type: 'text/plain' })

        let href = window.URL.createObjectURL(blob)
        let link = document.createElement('a')
        link.setAttribute('href', href)
        link.setAttribute('download', `videoTooltipIds.ts`)

        link.click()
    }

    return (
        <button onClick={_download}>
            Download Ids
        </button>
    )
}

interface IPassageList {
    toggleCollapse: () => void
    rt: Root
}

class PassageList extends Component<IPassageList> {
    passageCounts = new Map<string, number>()

    constructor(props: IPassageList) {
        super(props)
    }

    componentDidUpdate() {
        let { rt } = this.props
        let { passage, passageVideo } = rt

        if (!passage) return

        // If the number of videos for the current passage changes,
        // reset to the last video present to make sure the user
        // sees that there is a new video.

        let count1 = this.passageCounts.get(passage._id) || 0
        let count2 = passage.videos.length
        log('componentDidUpdate', passage._id, count1, count2)

        if (count2 !== 0 && count1 !== count2) {
            let activeVideos = passage.videosNotDeleted
            if (activeVideos.length > 0) {
                rt.setPassageVideo(activeVideos.slice(-1)[0])
            }
        }
    }

    render() {
        let { rt, toggleCollapse } = this.props
        let { project, portion, useMobileLayout, hashtag } = rt
        const removedPassages = portion?.removedPassages ?? []

        let initial = ''
        if (hashtag) {
            initial = hashtag
        } else if (portion) {
            initial = portion.name
        }

        let passages = (portion && portion.passages) || []
        if (hashtag) {
            passages = project.passagesMatchingHashtag(hashtag)
        }

        /* eslint-disable no-unused-expressions */
        passages.length // required to access length or list appears empty in view!!! why?

        // Remember how many videos each passage has
        this.passageCounts.clear()
        passages.forEach(item => this.passageCounts.set(item._id, item.videos.length))

        let values = _.pluck(project.portions, 'name')

        let isSLTTLoc = rt.project.name === 'SLTTLoc'

        return (
            <div>
                <div className="portion-selector-container" style={{ display: 'flex', alignItems: 'center' }}>
                    {localStorage.enablePassageListToggleButton === 'true' && <div>
                        <button
                            onClick={toggleCollapse}
                            type="button"
                            className="btn btn-default btn-primary"
                            title="Toggle Collapse"
                            style={{
                                height: '35px', // Adjust to visually match the select height
                                width: '40px', // Set width for a square button
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center', // Center the arrow icon
                                padding: '0', // Remove extra padding
                                margin: '0', // Ensure no extra spacing
                                marginTop: '-5px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <i className="fas fa-caret-up te-caret" style={{marginLeft: '11px'}}></i>
                        </button>
                    </div>}
                    <div className="portion-selector-div" style={{ marginLeft: '10px' }}>
                        <PortionSelector
                            rt={rt}
                            onChange={this.onChange.bind(this)}
                            values={values}
                            selectedValue={initial}
                        />
                    </div>
                </div>
                <div className="passages">
                    <SortableList
                        rt={rt}
                        items={passages}
                        onSortEnd={this.onSortEnd.bind(this)}
                        shouldCancelStart={() => useMobileLayout}
                        distance={5} />
                    {!hashtag && portion && !useMobileLayout &&
                        <PassageAdder rt={rt} />
                    }
                    {isSLTTLoc && <ExportTooltipVideoIds {...{ rt }} />}
                    {rt.iAmAdmin && <PassagesUnremover rt={rt} removedPassages={removedPassages} />}
                </div>
            </div>
        )
    }

    onChange(event: React.ChangeEvent<HTMLSelectElement>) {
        let { rt } = this.props
        let { project } = rt

        let portions: Array<Portion> = project.portions || []
        let name = event.target.value
        // the hashtags list comes after the portions list
        // (don't treat portion names that start with `#` as hashtags) 
        if (name.startsWith('#') && event.target.selectedIndex > (portions.length - 1)) {
            rt.hashtag = name
            rt.setPassage(null)
            return
        }

        rt.hashtag = ''

        let portion = _.findWhere(portions, { name })
        if (portion) {
            rt.setPortion(portion)
                .catch(displayError)
        } else {
            log(`### could not find portion [${name}]`)
        }
    }

    onSortEnd(indices: any) {
        let { rt } = this.props
        let { iAmTranslator, portion } = rt

        if (!iAmTranslator) return
        let _id = portion!.passages[indices.oldIndex]._id

        portion!.movePassage(_id, indices.newIndex)
            .catch(systemError)
    }
}

export default observer(PassageList)